import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import DOMPurify from 'dompurify';
import {
  FiMapPin,
  FiClock,
  FiBriefcase,
  FiFileText,
  FiCheckCircle,
  FiClipboard,
  FiStar,
  FiBook,
  FiAward,
  FiTag
} from 'react-icons/fi';
import http, { getUserFacingError } from '../services/http';
import { applicationService } from '../services/applicationService';
import { formatSalaryRange } from '../utils/formatSalary';
import { getJobCityName, JobCitySource } from '../utils/jobCity';
import '../scss/JobDetail.scss';

dayjs.extend(relativeTime);

interface Level { id?: number; name?: string | null; }
type OptionalLevel = Level | string | null | undefined;
interface Position { position_loker_id: number; position_loker_name: string; }
interface JobDetailModel extends JobCitySource {
  id: number;
  title: string;
  company: string;
  type: string;
  status: string;
  salary_min: number;
  salary_max: number;
  show_salary?: boolean;
  posted_date: string;
  experience?: OptionalLevel;
  experience_level?: OptionalLevel;
  education?: OptionalLevel;
  education_type?: OptionalLevel;
  education_level?: OptionalLevel;
  skills: Level[];
  positions?: Position[];
  description?: string | null;
  responsibilities?: string | null;
  requirements?: string | null;
  benefits?: string | null;
}

const getOptionalLevelName = (...values: OptionalLevel[]) => {
  for (const value of values) {
    const name = typeof value === 'string' ? value.trim() : value?.name?.trim();
    if (name) return name;
  }

  return null;
};

const hasContent = (value?: string | null) => Boolean(value?.trim());

const JobDetail: React.FC = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [job, setJob] = useState<JobDetailModel | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [errorRetryable, setErrorRetryable] = useState(false);
  const [isApplied, setIsApplied] = useState(false);
  const [isCheckingApplied, setIsCheckingApplied] = useState(false);
  const [isApplying, setIsApplying] = useState(false);
  const [applyError, setApplyError] = useState<string | null>(null);
  const sanitizedDescription = useMemo(
    () => DOMPurify.sanitize(job?.description || '', { USE_PROFILES: { html: true } }),
    [job?.description]
  );

  const fetchJobDetail = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      setErrorRetryable(false);
      const response = await http.get(`/jobs/${id}`);
      const jobData = response.data?.data;

      if (!jobData) {
        setError('Lowongan kerja tidak ditemukan');
        setJob(null);
        return;
      }

      setJob(jobData);
      setError(null);
    } catch (err: any) {
      const userFacingError = getUserFacingError(err, 'Gagal memuat detail pekerjaan.');
      setError(userFacingError.message);
      setErrorRetryable(userFacingError.retryable);
      setJob(null);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    if (id) {
      fetchJobDetail();
    }
  }, [fetchJobDetail, id]);

  useEffect(() => {
    if (!job?.id) return;

    setIsApplied(false);

    const token = localStorage.getItem('authToken');
    if (!token) {
      setIsCheckingApplied(false);
      return;
    }

    let isMounted = true;
    setIsCheckingApplied(true);

    http.get<{ status: boolean; data: number[] }>('auth/applied-jobs')
      .then((response) => {
        if (!isMounted) return;
        const appliedIds = Array.isArray(response.data?.data)
          ? response.data.data.map(Number)
          : [];
        setIsApplied(appliedIds.includes(Number(job.id)));
      })
      .catch(() => {
        if (!isMounted) return;
        setIsApplied(false);
      })
      .finally(() => {
        if (isMounted) setIsCheckingApplied(false);
      });

    return () => {
      isMounted = false;
    };
  }, [job?.id]);

  if (loading) return (
    <div className="job-detail__feedback">
      <div className="job-detail__spinner" />
      <p>Memuat detail pekerjaan...</p>
    </div>
  );
  if (error) return (
    <div className="job-detail__feedback job-detail__feedback--error">
      <FiBriefcase size={40} />
      <p>{error}</p>
      {errorRetryable ? (
        <button className="job-detail__apply" onClick={fetchJobDetail}>
          Coba Lagi
        </button>
      ) : (
        <button className="job-detail__apply" onClick={() => navigate('/lowongan-kerja')}>
          Kembali ke Lowongan
        </button>
      )}
    </div>
  );
  if (!job) return (
    <div className="job-detail__feedback">
      <p>Data tidak ditemukan.</p>
    </div>
  );

  const posted = dayjs(job.posted_date).fromNow();
  const experienceName = getOptionalLevelName(job.experience_level, job.experience);
  const educationName = getOptionalLevelName(job.education_level, job.education_type, job.education);
  const cityName = getJobCityName(job);
  const isApplyDisabled = isApplied || isCheckingApplied || isApplying;
  const applyLabel = isCheckingApplied
    ? 'Mengecek Status...'
    : isApplying
      ? 'Memproses...'
    : isApplied
      ? 'Sudah Melamar'
      : 'Lamar Sekarang';

  const handleApply = async () => {
    if (!localStorage.getItem('authToken')) {
      navigate('/login', { state: { from: `/lowongan-kerja/${job.id}` } });
      return;
    }

    setIsApplying(true);
    setApplyError(null);

    try {
      const response = await applicationService.apply(job.id);
      const result = response.data;

      setIsApplied(true);
      navigate('/terima-kasih', {
        state: {
          key: 'talent-form',
          from: 'job-apply',
          jobId: job.id,
          outsourceId: result.outsource_id,
          applicationId: result.application_id,
        },
      });
    } catch (caughtError: any) {
      const payload = caughtError?.response?.data;

      if (payload?.needs_profile) {
        navigate(`/form-talent/${job.id}`, {
          state: { jobId: job.id, completeProfile: true },
        });
        return;
      }

      if (payload?.already_applied) {
        setIsApplied(true);
        return;
      }

      const userFacingError = getUserFacingError(caughtError, 'Lamaran belum dapat diproses.');
      setApplyError(userFacingError.message);
    } finally {
      setIsApplying(false);
    }
  };

  return (
    <div className="job-detail">
      <div className="job-detail__hero">
        <div className="job-detail__hero-inner">
          <div className="job-detail__hero-left">
            <div className="job-detail__type-badge"><FiBriefcase size={11} />{job.type}</div>
            <h1 className="job-detail__title">{job.title}</h1>
            <div className="job-detail__meta">
              {cityName && (
                <span className="job-detail__meta-item">
                  <FiMapPin size={14} />
                  {cityName}
                </span>
              )}
              <span className="job-detail__meta-item">
                <FiClock size={14} />
                Diposting {posted}
              </span>
              {experienceName && (
                <span className="job-detail__meta-item">
                  <FiBriefcase size={14} />
                  {experienceName}
                </span>
              )}
            </div>
          </div>
          <div className="job-detail__hero-right">
            {job.show_salary !== false && (
              <div className="job-detail__salary">{formatSalaryRange(job.salary_min, job.salary_max)}</div>
            )}
            {job.status === 'closed' ? (
              <div className="job-detail__closed-notice">
                Lowongan Sudah Ditutup
              </div>
            ) : (
              <button
                className={`job-detail__apply${isApplyDisabled ? ' job-detail__apply--disabled' : ''}`}
                onClick={isApplyDisabled ? undefined : handleApply}
                disabled={isApplyDisabled}
                aria-label={applyLabel}
              >
                {!isApplyDisabled && (
                  <span className="job-detail__apply-icon-slot" aria-hidden="true">
                    <FiStar size={14} />
                  </span>
                )}
                <span className="job-detail__apply-text-slot">{applyLabel}</span>
              </button>
            )}
            {applyError && <p className="job-detail__apply-error">{applyError}</p>}
          </div>
        </div>
      </div>

      <div className="job-detail__container">
        <main className="job-detail__main">
          <section className="job-detail__section">
            <div className="job-detail__section-header">
              <span className="job-detail__section-icon" style={{ background: '#EEF9F8' }}><FiFileText color="#2EC4B6" /></span>
              <h3>Deskripsi Pekerjaan</h3>
            </div>
            <div
              className="job-detail-description"
              dangerouslySetInnerHTML={{ __html: sanitizedDescription }}
            />
          </section>

          {hasContent(job.responsibilities) && (
            <section className="job-detail__section">
              <div className="job-detail__section-header">
                <span className="job-detail__section-icon" style={{ background: '#EEF2FF' }}><FiCheckCircle color="#6366F1" /></span>
                <h3>Tanggung Jawab</h3>
              </div>
              <p className="job-detail__section-text">{job.responsibilities}</p>
            </section>
          )}

          {hasContent(job.requirements) && (
            <section className="job-detail__section">
              <div className="job-detail__section-header">
                <span className="job-detail__section-icon" style={{ background: '#FFF7ED' }}><FiClipboard color="#F97316" /></span>
                <h3>Persyaratan</h3>
              </div>
              <p className="job-detail__section-text">{job.requirements}</p>
            </section>
          )}

          {hasContent(job.benefits) && (
            <section className="job-detail__section">
              <div className="job-detail__section-header">
                <span className="job-detail__section-icon" style={{ background: '#FFF0F6' }}><FiStar color="#EC4899" /></span>
                <h3>Keuntungan</h3>
              </div>
              <p className="job-detail__section-text">{job.benefits}</p>
            </section>
          )}
        </main>

        <aside className="job-detail__sidebar">
          {educationName && (
            <div className="job-detail__side-block">
              <div className="job-detail__side-block-title">
                <FiBook size={16} />
                <h4>Pendidikan</h4>
              </div>
              <span className="job-detail__info-chip">{educationName}</span>
            </div>
          )}

          {(job.skills || []).length > 0 && (
            <div className="job-detail__side-block">
              <div className="job-detail__side-block-title">
                <FiAward size={16} />
                <h4>Keahlian</h4>
              </div>
              <div className="job-detail__skills">
                {job.skills.map((s) => <span key={s.id} className="skill">{s.name}</span>)}
              </div>
            </div>
          )}

          {(job.positions || []).length > 0 && (
            <div className="job-detail__side-block">
              <div className="job-detail__side-block-title">
                <FiTag size={16} />
                <h4>Posisi yang Dibuka</h4>
              </div>
              <div className="job-detail__positions">
                {(job.positions || []).map((p) => (
                  <span key={p.position_loker_id} className="job-detail__position-chip">{p.position_loker_name}</span>
                ))}
              </div>
            </div>
          )}
        </aside>
      </div>
    </div>
  );
};

export default JobDetail;
